import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import AuthProvider from "./components/AuthProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import "./i18n";
import "./index.css";
import Conclusion from "./pages/Conclusion";
import FinishRegistration from "./pages/FinishRegistration";
import Login from "./pages/Login";
import UploadVideo from "./pages/UploadVideo";

import ReactGA from "react-ga4";
import { getGlobalUserId, initAmplitude, initExperiment } from "./lib/amplitude";
import AuthDbUser from "./components/AuthDbUser";
import TaggedPlace from "./components/TaggedPlace";
import FinishRedeem from "./pages/FinishRedeem";
import CouponRedeemed from "./pages/CouponRedeemed";
import LogOut from "./pages/Logout";
import OnboardingFC from "./pages/OnboardingFC";
import TagPlace from "./pages/TagPlace";
import ScanQRCode from "./pages/ScanQRCode";
import Guidelines from "./pages/Guidelines";
import ReviewRejected from "./pages/ReviewRejected";
import ReviewStatusGuard from "./components/ReviewStatusGuard";
import Onboarding from "./pages/Onboarding";
import LinkTreeLanding from "./pages/LinkTreeLanding";

// Only initialize Sentry in production
if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://f757d840eac16148c49ab4c16cdfccc4@o4508715857084417.ingest.de.sentry.io/4508715886510160",
    integrations: [Sentry.browserTracingIntegration()],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

// Only initialize Google Analytics in production
if (import.meta.env.PROD) {
  ReactGA.initialize("G-PTV065NRXF");
}

// Initialize Amplitude analytics
const amplitudeApiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
if (amplitudeApiKey) {
  const globalUserId = getGlobalUserId();
  initAmplitude(amplitudeApiKey, globalUserId);
}

// Initialize Amplitude Experiment
const experimentKey = import.meta.env.VITE_AMPLITUDE_EXPERIMENT_KEY;
if (experimentKey) {
  initExperiment(experimentKey);
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: Infinity } },
});

const router = createBrowserRouter([
  {
    path: "/guidelines",
    element: <Guidelines />,
  },
  {
    path: "/fc/:fcId",
    element: <OnboardingFC />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/logout",
    element: <LogOut />,
  },
  {
    path: "/scan",
    element: (
      <ProtectedRoute>
        <ScanQRCode />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coupon-redeemed",
    element: (
      <ProtectedRoute>
        <ReviewStatusGuard>
          <CouponRedeemed />
        </ReviewStatusGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/review-rejected",
    element: (
      <ProtectedRoute>
        <ReviewRejected />
      </ProtectedRoute>
    ),
  },
  {
    path: "/tag-place",
    element: (
      <ProtectedRoute>
        <AuthDbUser>
          <TagPlace />
        </AuthDbUser>
      </ProtectedRoute>
    ),
  },
  {
    path: "/upload",
    element: (
      <ProtectedRoute>
        <AuthDbUser>
          <TaggedPlace>
            <UploadVideo />
          </TaggedPlace>
        </AuthDbUser>
      </ProtectedRoute>
    ),
  },
  {
    path: "/",
    element: <Onboarding />,
  },
  {
    path: "/success",
    element: (
      <ProtectedRoute>
        <Conclusion />
      </ProtectedRoute>
    ),
  },

  {
    path: "/success-redeem",
    element: (
      <ProtectedRoute>
        <ReviewStatusGuard>
          <FinishRedeem />
        </ReviewStatusGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/finish-registration",
    element: (
      <ProtectedRoute>
        <FinishRegistration />
      </ProtectedRoute>
    ),
  },
  {
    path: "/l/:slug",
    element: <LinkTreeLanding />,
  },
]);

createRoot(document.getElementById("root")!).render(
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  </QueryClientProvider>,
);
